
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { LoginForm } from "@/components/auth/LoginForm";
import { SignupForm } from "@/components/auth/SignupForm";
import { useAuth } from "@/components/AuthProvider";
import { Loader2 } from "lucide-react";
import { toast } from "@/components/ui/use-toast";

type AuthMode = "login" | "signup";

const Auth = () => {
  const [mode, setMode] = useState<AuthMode>("login");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  
  useEffect(() => {
    // Check if user is already logged in
    if (user && !loading) {
      navigate("/");
      return;
    }
    
    // Check for email confirmation response
    const error = searchParams.get("error");
    const error_description = searchParams.get("error_description");
    
    // Check for successful email verification
    const access_token = searchParams.get("access_token");
    const refresh_token = searchParams.get("refresh_token");
    const type = searchParams.get("type");
    
    if (access_token && refresh_token && type === "signup") {
      // Email verification was successful
      toast({
        title: "Email Verified!",
        description: "Your email has been successfully verified. You can now log in.",
      });
      setMode("login"); // Switch to login mode
    }
    else if (error) {
      console.error("Auth error:", error, error_description);
      toast({
        variant: "destructive",
        title: "Authentication Error",
        description: error_description || "There was a problem with authentication",
      });
    }
  }, [user, loading, navigate, searchParams]);

  const switchMode = () => {
    setMode(mode === "login" ? "signup" : "login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-madts-purple" />
        <span className="ml-2 text-lg">Loading...</span>
      </div>
    );
  }

  return (
    <AuthLayout
      title={mode === "login" ? "Welcome Back!" : "Join MAD-T's"}
      description={
        mode === "login" 
          ? "Sign in to your account to continue" 
          : "Create an account to get started with MAD-T's"
      }
    >
      {mode === "login" ? (
        <LoginForm onModeSwitch={switchMode} />
      ) : (
        <SignupForm 
          onSuccess={() => setMode("login")}
          onModeSwitch={switchMode}
        />
      )}
    </AuthLayout>
  );
};

export default Auth;
